import "./styles/global.css";

import { Provider } from "@/components/ui/provider";
import React from "react";
import ReactDOM from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import { BrowserRouter } from "react-router-dom";
import {
  QueryClient,
  QueryClientProvider,
  QueryCache,
  MutationCache,
} from "@tanstack/react-query";
import { toaster } from "@/components/ui/toaster";
import App from "./app";
import * as Sentry from "@sentry/react";

const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => {
      // Don't show global error toast alerts for expected auth/session checks or normal fetch falls
      if (
        error.message.includes("JWT") ||
        error.message.includes("auth") ||
        error.message.includes("anonymous")
      ) {
        return;
      }
      toaster.create({
        title: "Database Sync Error",
        description: error.message,
        type: "error",
      });
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      toaster.create({
        title: "Action Synchronization Failed",
        description: error.message,
        type: "error",
      });
    },
  }),
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 1000 * 60 * 5, // 5 minutes standard caching
    },
  },
});

const sentryDsn = import.meta.env.VITE_SENTRY_DSN;

if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    sendDefaultPii: true,
    integrations: [Sentry.replayIntegration()],
    // Session Replay
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
} else {
  console.warn("Sentry DSN missing. Skipping Sentry initialization.");
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <HelmetProvider>
      <Provider>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </QueryClientProvider>
      </Provider>
    </HelmetProvider>
  </React.StrictMode>,
);
